import React, { useMemo } from 'react';
import block from 'bem-cn';
import { useSelector, shallowEqual } from 'react-redux';

import Spinner from 'components/Spinner/mobile';
import { LineChart } from 'components/Charts/LineChart';
import SVG from 'components/SVG';

import useModel, { EStatisticsTab, ETableType } from 'features/myStatistics/model/useModel';

import usersSVG from './img/users.svg';
import totalBetsSVG from './img/totalBets.svg';
import totalNetwinSVG from './img/totalNetwin.svg';
import totalWinSVG from './img/totalWin.svg';
import activeUserSVG from './img/active-user.svg';
import arrowSVG from './img/arrow.svg';
import crossSVG from './img/cross.svg';
import marginSVG from './img/margin.svg';
import infoSVG from './img/info.svg';

import Filters from './Filters';

import './MyStatistic.scss';
import Table from './Table';
import Switcher from 'components/Switcher';
import { formatNumber } from 'shared/utils/formatNumber';
import TableBalance from '../mobile/TableBalance';

const b = block('my-statistic-mobile');

const mobileTabs = [EStatisticsTab.agentsStatistics];

const MyStatistic = () => {
  const model = useModel();

  const { actionProcessing, statistic, balanceStatistic } = useSelector(state => state.myStatistics, shallowEqual);
  const locale = useSelector(state => state.locale.locale, shallowEqual);
  const role = useSelector(state => state.auth.role, shallowEqual);

  const formatDifferenceItem = percent => {
    const flag = Number(percent) === 0 ? '' : Number(percent) > 0 ? '↑' : '↓';

    return (
      <div className={b('difference-item', { raise: Number(percent) > 0, empty: flag === '' })}>
        <span className={b('difference-flag')}>{flag}</span> {`${percent} %`}
      </div>
    );
  };

  const isMobile = window.innerWidth < 490;

  const svgMap = {
    users: usersSVG,
    activeUsers: activeUserSVG,
    totalBets: totalBetsSVG,
    totalGGR: totalNetwinSVG,
    totalWins: totalWinSVG,
    margin: marginSVG,
  };

  const topItems = model.inputState.buildTopItems(locale);

  const {
    inputState: { tabs, setActiveTab, setIsAsc, isAsc },
    outputState,
  } = model;

  const convertedTabs = tabs.filter(tab => mobileTabs.includes(tab.value));

  const activeTab = useMemo(() => model.inputState.activeTab, [model.inputState.activeTab]);

  const tableComponent = useMemo(() => {
    switch (activeTab) {
      case EStatisticsTab.agentsStatistics:
        return (
          <div className={b('content')}>
            <Table data={statistic.ggrRanking} type={activeTab} />
          </div>
        );
      case EStatisticsTab.agentsBalance:
        return (
          <div className={b('content')}>
            <TableBalance data={balanceStatistic} setIsAsc={setIsAsc} isAsc={isAsc} />
          </div>
        );
      case EStatisticsTab.playersBalance:
        return (
          <div className={b('content')}>
            <TableBalance data={balanceStatistic} setIsAsc={setIsAsc} isAsc={isAsc} />
          </div>
        );
    }
  }, [activeTab, balanceStatistic, statistic, setIsAsc, isAsc]);

  const tabsComponent = useMemo(() => {
    if (activeTab.includes(ETableType.Statistics))
      return convertedTabs.map(tab => {
        const onChange = () => {
          setActiveTab(tab.value);
        };

        return (
          <div onClick={onChange} className={b('tab', { active: tab.active })}>
            <span className={b('tab-title')}>{tab.title}</span>
          </div>
        );
      });

    return (
      <div className={b('tab', { active: true })}>
        <span>{tabs.find(v => v.active)?.title}</span>
        <SVG className={b('close')} svgProps={{ svg: crossSVG }} onClick={() => setActiveTab(EStatisticsTab.agentsStatistics)} />
      </div>
    );
  }, [convertedTabs, tabs, activeTab, setActiveTab]);

  return (
    <div className={b()}>
      <Spinner isLoading={model.outputState.actionProcessing} />
      <div className={b('title')}>{locale.myStatistic}</div>
      <div className={b('indicators-list')}>
        {topItems.map(item => (
          <div key={item.key} className={b('indicator')}>
            <div className={b('indicator-left')}>
              <SVG className={b('indicator-icon')} svgProps={{ svg: svgMap[item.icon] }} />
              <div className={b('indicator-main')}>
                <div className={b('indicator-value')}>{item.left.value}</div>
                <div className={b('indicator-label')}>{item.left.label}</div>
              </div>

              <div className={b('indicator-right')}>
                {item.info && (
                  <div className={b('info')}>
                    <span className={b('info-text')}>info</span>
                    <SVG className={b('info-icon')} svgProps={{ svg: infoSVG }} />
                    <div className={b('info-description')}>{item.info.description}</div>
                  </div>
                )}
                {item.isComparative && (
                  <div className={b('indicator-compare')}>
                    {locale.compare}
                    <div className={b('indicator-switcher')}>
                      <Switcher
                        key={item.key}
                        isActive={model.outputState.comparisonType === item.compareKey}
                        onSwitch={() => model.inputState.onChangeComparisonType(item.compareKey)}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>

            {!model.outputState.actionProcessing && item.showRight && item.isComparative && (
              <div className={b('difference')}>
                {formatDifferenceItem(item.diffPercent)}
                <div>{formatNumber(item.diff)}</div>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className={b('chart')}>
        <div className={b('chart-filters')}>
          <Filters model={model} />
        </div>

        <div className={b('chart-content')}>
          <LineChart
            title={model.outputState.data.title}
            labels={model.outputState.data?.time || []}
            originalLabels={model.outputState.data?.originalTime || []}
            colorFill={model.outputState.data?.colorFill}
            color={model.outputState.data?.color}
            data={model.outputState.data?.data || []}
            secondData={model.outputState.data2?.data || []}
            secondColor={model.outputState.data2?.color}
            secondColorFill={model.outputState.data2?.colorFill}
            originalLabels2={model.outputState.data2?.originalTime || []}
            isMobile={isMobile}
          />
        </div>
      </div>

      {role > 1 && <div className={b('balance')}>
        <div className={b('balance-header')}>
          {locale.balanceOfPlayers}
          <div
            className={b('balance-button')}
            onClick={() => {
              setActiveTab(EStatisticsTab.playersBalance);
            }}>
            {locale.more}
            <SVG className={b('balance-icon')} svgProps={{ svg: arrowSVG }} />
          </div>
        </div>
        <div className={b('balance-amount')}>{formatNumber(statistic.balanceStatistic.balanceOfUser)}</div>
      </div>}

      <div className={b('balance')}>
        <div className={b('balance-header')}>
          {locale.balanceOf[role - 1]}
          <div
            className={b('balance-button')}
            onClick={() => {
              setActiveTab(EStatisticsTab.agentsBalance);
            }}>
            {locale.more}
            <SVG className={b('balance-icon')} svgProps={{ svg: arrowSVG }} />
          </div>
        </div>
        <span className={b('balance-amount')}>{formatNumber(outputState.childrenBalance)}</span>
      </div>

      <div className={b('table-wrapper')}>
        <div className={b('tabs')}>{tabsComponent}</div>
        <div className={b('table')}>
          {tableComponent}
        </div>
      </div>
    </div>
  );
};

export default MyStatistic;
